import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { testPrompt, testPromptChat, optimizePrompt, type ChatMessage, getModelInfo, DEFAULT_GEMINI_MODEL } from '../services/geminiService';

import { translateText } from '../services/translationService';
import { usePromptContext } from '../src/context/PromptContext';
import { QuickModelSelector } from './ModelSelector';
import Modal from './Modal';
import VariableSubstitutionModal, { extractVariableDefinitions } from './VariableSubstitutionModal';

import { FlaskIcon, WandIcon, PencilIcon, SparklesIcon, ClipboardCopyIcon, TranslateIcon, Loader2Icon, XIcon, EyeIcon, SendIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '../constants';

interface PromptActionsProps {
  promptId: string;
  content: string;
  searchTerm: string;
  settings?: { 
    globalSystemInstruction: string;
    promptOptimizerSystemPrompt?: string;
    selectedProviderId?: string;
    testModel?: string;
    generatorModel?: string;
    optimizerModel?: string;
    customProviders?: any[];
    customGeminiModels?: string[];
  };
  onChangeContent: (content: string) => void;
  onTrackPromptUse: (id: string) => void;
  /** Überschrift über dem Textfeld, z. B. „Anweisungen (SKILL.md)“ */
  label?: string;
  placeholderText?: string;
  /** Jede Erhöhung löst „Kopieren“ aus, z. B. vom Kopier-Button im Editorkopf */
  copySignal?: number;
  onUpdateSettings?: (settings: any) => void;
}

type CopyMode = 'normal' | 'english';

/** Persistent store: persists placeholder values per promptId using localStorage */
const getStoredPlaceholderValues = (promptId: string): Record<string, string> => {
  try {
    const allStored = localStorage.getItem('prompt_placeholder_cache');
    if (!allStored) return {};
    const parsed = JSON.parse(allStored);
    return parsed[promptId] || {};
  } catch {
    return {};
  }
};

const saveStoredPlaceholderValues = (promptId: string, values: Record<string, string>) => {
  try {
    const allStored = localStorage.getItem('prompt_placeholder_cache');
    const parsed = allStored ? JSON.parse(allStored) : {};
    parsed[promptId] = values;
    localStorage.setItem('prompt_placeholder_cache', JSON.stringify(parsed));
  } catch {
    // Ignore
  }
};

/** Escape HTML special characters to prevent XSS in rendered markdown preview */
const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c] || c));

/** Simple Markdown → HTML converter (safe against XSS) */
const renderMarkdown = (text: string): string => {
  return escapeHtml(text)
    // Code blocks (must come before inline code)
    .replace(/```([\s\S]*?)```/g, '<pre class="overflow-x-auto bg-gray-100 dark:bg-gray-900 rounded-lg p-3 my-2 text-xs font-mono whitespace-pre">$1</pre>')
    // Headings
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold mt-4 mb-1 text-gray-800 dark:text-gray-100">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-4 mb-1 text-gray-800 dark:text-gray-100">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white">$1</h1>')
    // Bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-indigo-600 dark:text-indigo-400">$1</code>')
    // Unordered lists
    .replace(/^[\-\*] (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br/>');
};

const PromptActions: React.FC<PromptActionsProps> = ({ promptId, content, searchTerm, settings, onChangeContent, onTrackPromptUse, onUpdateSettings, label = 'Prompt-Text', placeholderText = 'Prompt hier schreiben oder einfügen …', copySignal = 0 }) => {


  const [isLoading, setIsLoading] = useState(false);
  const [testChatHistory, setTestChatHistory] = useState<ChatMessage[]>([]);
  const [testFollowUp, setTestFollowUp] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationHistory, setOptimizationHistory] = useState<ChatMessage[]>([]);
  const [optimizationInput, setOptimizationInput] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationForCopy, setTranslationForCopy] = useState<string | null>(null);
  const [newPlaceholderName, setNewPlaceholderName] = useState('');
  const [isMarkdownPreview, setIsMarkdownPreview] = useState(false);
  const [expandedOptHistory, setExpandedOptHistory] = useState<Record<number, boolean>>({});
  
  // Abort controllers
  const testAbortRef = useRef<AbortController | null>(null);
  const optimizeAbortRef = useRef<AbortController | null>(null);
  
  // Track currently loaded promptId to prevent saving stale values on prompt switch
  const loadedPromptIdRef = useRef(promptId);

  // Load saved values for the current promptId
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>(
    () => getStoredPlaceholderValues(promptId)
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { showToast } = usePromptContext();

  // When promptId changes, load its stored values
  useEffect(() => {
    loadedPromptIdRef.current = promptId;
    setPlaceholderValues(getStoredPlaceholderValues(promptId));
  }, [promptId]);

  // Persist values back to localStorage whenever they change, only if state belongs to current promptId
  useEffect(() => {
    if (promptId && loadedPromptIdRef.current === promptId && Object.keys(placeholderValues).length > 0) {
      saveStoredPlaceholderValues(promptId, placeholderValues);
    }
  }, [placeholderValues, promptId]);

  // Adjust textarea height on mount and content change (external)
  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 1000) + 'px';
      const overlay = el.previousElementSibling as HTMLElement;
      if (overlay) overlay.style.height = el.style.height;
    }
  }, [content]);

  // -- Placeholder copy modal state --
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [copyMode, setCopyMode] = useState<CopyMode>('normal');

  const placeholderInfo = useMemo(() => {
    return extractVariableDefinitions(content || '').map((def) => ({
      key: def.key,
      default: def.default || '',
    }));
  }, [content]);

  const placeholderKeys = useMemo(() => placeholderInfo.map(info => info.key), [placeholderInfo]);

  const hasPlaceholders = placeholderKeys.length > 0;

  const handleTestPrompt = async () => {
    if (!content.trim()) return;
    onTrackPromptUse(promptId);
    setIsLoading(true);
    setTestChatHistory([]);
    setTestFollowUp('');

    const controller = new AbortController();
    testAbortRef.current = controller;

    try {
      const result = await testPrompt(content, settings?.globalSystemInstruction, settings, controller.signal);
      // Initialize chat history for follow-up
      setTestChatHistory([
        { role: 'user', content: content },
        { role: 'assistant', content: result },
      ]);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        showToast('Test abgebrochen.', 'error');
      } else {
        showToast('Fehler beim Testen.', 'error');
      }
    } finally {
      setIsLoading(false);
      testAbortRef.current = null;
    }
  };

  const handleTestFollowUp = async () => {
    if (!testFollowUp.trim() || testChatHistory.length === 0) return;
    setIsLoading(true);

    const controller = new AbortController();
    testAbortRef.current = controller;

    const updatedHistory: ChatMessage[] = [...testChatHistory, { role: 'user', content: testFollowUp }];
    setTestChatHistory(updatedHistory);
    setTestFollowUp('');

    try {
      const result = await testPromptChat(updatedHistory, settings?.globalSystemInstruction, settings, controller.signal);
      const fullHistory: ChatMessage[] = [...updatedHistory, { role: 'assistant', content: result }];
      setTestChatHistory(fullHistory);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        showToast('Anfrage abgebrochen.', 'error');
        // Remove the pending user message
        setTestChatHistory(testChatHistory);
      } else {
        showToast('Fehler bei der Folgefrage.', 'error');
        setTestChatHistory(testChatHistory);
      }
    } finally {
      setIsLoading(false);
      testAbortRef.current = null;
    }
  };

  const handleAbortTest = useCallback(() => {
    testAbortRef.current?.abort();
  }, []);

  const handleOptimizePrompt = async () => {
    if (!content.trim() && optimizationHistory.length === 0) return;
    if (optimizationHistory.length > 0 && !optimizationInput.trim()) return;
    setIsOptimizing(true);
    
    const controller = new AbortController();
    optimizeAbortRef.current = controller;

    const currentHistory = [...optimizationHistory];
    
    if (currentHistory.length === 0) {
      const firstMessage = optimizationInput.trim() 
        ? `${content}\n\n---\nZusätzliche Optimierungsanweisung:\n${optimizationInput.trim()}`
        : content;
      currentHistory.push({ role: 'user', content: firstMessage });
    } else {
      if (optimizationInput.trim()) {
        currentHistory.push({ role: 'user', content: optimizationInput.trim() });
      }
    }
    
    setOptimizationHistory(currentHistory);
    setOptimizationInput('');

    try {
      const result = await optimizePrompt(currentHistory, settings?.promptOptimizerSystemPrompt, settings, controller.signal);
      setOptimizationHistory([...currentHistory, { role: 'assistant', content: result }]);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        showToast('Optimierung abgebrochen.', 'error');
      } else {
        showToast('Fehler bei der Optimierung.', 'error');
      }
    } finally {
      setIsOptimizing(false);
      optimizeAbortRef.current = null;
    }
  };

  const handleAbortOptimize = useCallback(() => {
    optimizeAbortRef.current?.abort();
  }, []);

  const handleCopyToClipboard = useCallback((text: string, message: string, track = true) => {
    navigator.clipboard.writeText(text).then(
      () => { showToast(message); if (track) onTrackPromptUse(promptId); },
      err => {
        console.error('Could not copy text: ', err);
        showToast('Kopieren nicht möglich. Bitte Zugriff auf die Zwischenablage erlauben.', 'error');
      }
    );
  }, [showToast, onTrackPromptUse, promptId]);

  // ── Copy button click handler ──
  const handleCopyClick = () => {
    if (hasPlaceholders) {
      setCopyMode('normal');
      setShowPlaceholderModal(true);
    } else {
      handleCopyToClipboard(content, 'Prompt kopiert');
    }
  };

  const lastCopySignal = useRef(copySignal);
  useEffect(() => {
    if (copySignal === lastCopySignal.current) return;
    lastCopySignal.current = copySignal;
    handleCopyClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copySignal]);

  // ── English-copy button click handler ──
  const handleEnglishCopyClick = async () => {
    if (!content.trim() || isTranslating) return;
    if (hasPlaceholders) {
      setCopyMode('english');
      setShowPlaceholderModal(true);
    } else {
      // No placeholders → translate directly
      setIsTranslating(true);
      try {
        const englishPrompt = await translateText(content, 'de', 'en');
        setTranslationForCopy(englishPrompt);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Übersetzung fehlgeschlagen.';
        showToast(message, 'error');
      } finally {
        setIsTranslating(false);
      }
    }
  };

  const handleConfirmCopy = () => {
    if (!translationForCopy) return;
    handleCopyToClipboard(translationForCopy, 'Englische Fassung kopiert');
    setTranslationForCopy(null);
  };

  const insertPlaceholder = () => {
    const name = newPlaceholderName.trim().replace(/\s+/g, '_');
    if (!name) {
      showToast('Bitte einen Platzhalter-Namen eingeben.', 'error');
      return;
    }

    const token = `{{${name}}}`;
    const el = textareaRef.current;

    if (!el) {
      onChangeContent(`${content}${content.endsWith(' ') || !content ? '' : ' '}${token}`);
    } else {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const updatedContent = `${content.slice(0, start)}${token}${content.slice(end)}`;
      onChangeContent(updatedContent);
      requestAnimationFrame(() => {
        el.focus();
        const newCursorPos = start + token.length;
        el.setSelectionRange(newCursorPos, newCursorPos);
      });
    }

    setPlaceholderValues((prev) => ({ ...prev, [name]: prev[name] ?? '' }));
    setNewPlaceholderName('');
    showToast(`Platzhalter ${token} eingefügt.`);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <h3 className="label-xs">{label}</h3>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Markdown preview toggle */}
          <button
            onClick={() => setIsMarkdownPreview(p => !p)}
            className={`flex items-center gap-1.5 text-xs h-8 px-2.5 rounded-md font-medium transition-colors ${
              isMarkdownPreview
                ? 'bg-kind-prompt/10 text-kind-prompt'
                : 'text-graphite hover:text-ink hover:bg-ink/[0.06]'
            }`}
            aria-pressed={isMarkdownPreview}
            title={isMarkdownPreview ? 'Bearbeiten' : 'Markdown-Vorschau'}
          >
            {isMarkdownPreview ? (
              <>
                <PencilIcon className="h-4 w-4" />
                <span>Bearbeiten</span>
              </>
            ) : (
              <>
                <EyeIcon className="h-4 w-4" />
                <span>Vorschau</span>
              </>
            )}
          </button>
          <div className="w-px h-4 bg-rule" aria-hidden="true" />
          <button
            onClick={handleCopyClick}
            aria-label="Prompt in die Zwischenablage kopieren"
            className="flex items-center gap-1.5 text-xs font-medium h-8 px-2.5 rounded-md text-graphite hover:text-ink hover:bg-ink/[0.06] transition-colors"
          >
            <ClipboardCopyIcon className="h-4 w-4" />
            <span>Kopieren</span>
            {hasPlaceholders && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-kind-prompt/10 text-kind-prompt font-mono text-[10px] font-semibold" title="Platzhalter werden beim Kopieren abgefragt">{placeholderKeys.length}</span>
            )}
          </button>
          <button
            onClick={handleEnglishCopyClick}
            disabled={isTranslating || !content.trim()}
            aria-label="Prompt ins Englische übersetzen und kopieren"
            className="flex items-center justify-center gap-1.5 text-xs font-medium h-8 px-2.5 rounded-md text-graphite hover:text-ink hover:bg-ink/[0.06] disabled:opacity-40 disabled:cursor-not-allowed w-full sm:w-auto transition-colors"
            title="Prompt ins Englische übersetzen und kopieren"
          >
            {isTranslating ? (
              <Loader2Icon className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <TranslateIcon className="h-4 w-4" />
                <span>Englisch kopieren</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Platzhalter: standardmäßig eingeklappt, erkannte Platzhalter bleiben sichtbar */}
      <details className="group mb-3 rounded-lg border border-rule bg-sheet open:bg-raised">
        <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none text-xs text-graphite hover:text-ink list-none [&::-webkit-details-marker]:hidden">
          <svg className="h-3 w-3 transition-transform group-open:rotate-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
          <span className="font-medium">Platzhalter</span>
          {hasPlaceholders ? (
            <span className="font-mono text-[11px] text-ink">{placeholderKeys.map(k => `{{${k}}}`).join('  ')}</span>
          ) : (
            <span>z. B. <code className="font-mono">{'{{thema}}'}</code> – Werte werden beim Kopieren abgefragt</span>
          )}
        </summary>
      <div className="px-3 pb-3 space-y-3">
        <p className="text-xs text-graphite">
          Platzhalter nutzen: z.&nbsp;B. <code className="font-mono bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{'{{produkt}}'}</code> im Text setzen.
          Beim Kopieren werden Sie aufgefordert, Werte einzugeben — der Original-Prompt bleibt erhalten.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newPlaceholderName}
            onChange={(e) => setNewPlaceholderName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); insertPlaceholder(); } }}
            placeholder="Name, z. B. produkt"
            className="input flex-1 font-mono"
            aria-label="Name des Platzhalters"
          />
          <button
            type="button"
            onClick={insertPlaceholder}
            className="btn btn-secondary btn-sm"
          >
            An Cursor einfügen
          </button>
        </div>

        {/* Show detected placeholders as badges */}
        {hasPlaceholders && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {placeholderKeys.map((key) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-kind-prompt/10 text-kind-prompt font-mono"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 8l-4 4 4 4"/><path d="M17 8l4 4-4 4"/></svg>
                {key}
                {(placeholderValues[key] ?? '').trim() && (
                  <span className="text-green-600 dark:text-green-400 text-[10px]">✓</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      </details>

      {/* Textarea with Highlight Overlay — or Markdown Preview */}
      {isMarkdownPreview ? (
        <div
          className="w-full min-h-48 p-4 rounded-lg bg-raised border border-rule text-[15px] leading-relaxed text-ink overflow-y-auto prose-sm animate-view-transition"
          dangerouslySetInnerHTML={{ __html: '<p class="mb-2">' + renderMarkdown(content || '_Kein Inhalt_') + '</p>' }}
        />
      ) : (
        <div className="relative w-full">
          {/* Highlight Layer */}
          <div
            className="absolute inset-x-0 top-0 p-4 rounded-lg border border-transparent pointer-events-none whitespace-pre-wrap break-words font-mono text-[14px] leading-[1.7] text-transparent overflow-hidden select-none z-0"
            aria-hidden="true"
          >
            {searchTerm && content.toLowerCase().includes(searchTerm.toLowerCase())
              ? content.split(new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')).map((part, i) =>
                  part.toLowerCase() === searchTerm.toLowerCase() ? (
                    <mark key={i} className="bg-amber-300/30 dark:bg-amber-400/20 rounded-sm text-transparent ring-1 ring-amber-400/20 shadow-[0_0_8px_rgba(251,191,36,0.1)]">
                      {part}
                    </mark>
                  ) : part
                )
              : content
            }
          </div>

          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              onChangeContent(e.target.value);
              const el = e.target;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 1000) + 'px';
              const overlay = el.previousElementSibling as HTMLElement;
              if (overlay) overlay.style.height = el.style.height;
            }}
            onScroll={(e) => {
              const overlay = e.currentTarget.previousElementSibling as HTMLElement;
              if (overlay) overlay.scrollTop = e.currentTarget.scrollTop;
            }}
            onFocus={(e) => {
              if (window.innerWidth < 640) {
                e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
            placeholder={placeholderText}
            className="w-full max-w-full min-h-56 sm:min-h-72 p-4 rounded-lg bg-raised/60 border border-rule focus:border-kind-prompt focus:ring-[3px] focus:ring-kind-prompt/15 focus:outline-none font-mono text-[14px] leading-[1.7] text-ink placeholder:text-graphite/70 caret-kind-prompt resize-y touch-pan-y relative z-[1] transition-colors"
            style={{ WebkitOverflowScrolling: 'touch' }}
            aria-label={label}
            aria-multiline="true"
          />

          {searchTerm && content.toLowerCase().includes(searchTerm.toLowerCase()) && (
            <div className="absolute top-2 right-4 z-10 flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 dark:bg-amber-900/40 rounded shadow-sm border border-amber-200/50 pointer-events-none animate-in fade-in zoom-in duration-200">
              <div className="w-2.5 h-2.5 text-amber-500"><SparklesIcon /></div>
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-tight">Treffer</span>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4 mt-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={handleTestPrompt}
            disabled={isLoading || isOptimizing || !content.trim()}
            className="btn btn-secondary"
          >
            {isLoading ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <FlaskIcon className="h-4 w-4" />}
            <span>{isLoading ? 'Wird getestet …' : 'Mit KI testen'}</span>
          </button>

          <div className="flex items-center gap-2">
             <QuickModelSelector 
                currentModel={settings?.testModel || (!settings?.selectedProviderId || settings?.selectedProviderId === 'gemini' ? DEFAULT_GEMINI_MODEL : '')} 
                onSelect={(m) => onUpdateSettings?.({ testModel: m })}
                providerId={settings?.selectedProviderId || 'gemini'}
                customProviders={settings?.customProviders || []}
                customGeminiModels={settings?.customGeminiModels || []}
             />
             <div className="text-xs text-graphite hidden lg:block">
               {getModelInfo(settings?.testModel || '')?.description}
             </div>
          </div>

          {isLoading && (
            <button
              onClick={handleAbortTest}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 font-medium hover:bg-red-200 dark:hover:bg-red-500/25 transition-colors text-sm active:scale-95"
            >
              <XIcon />
              <span>Abbrechen</span>
            </button>
          )}
        </div>

        <div className="p-4 rounded-lg bg-sheet border border-rule">
            <h3 className="label-xs mb-1.5">Mit KI verbessern</h3>
            <p className="text-xs text-graphite mb-3">Beschreiben Sie, was sich ändern soll. Jeder Vorschlag erscheint unten und lässt sich übernehmen oder weiter verfeinern.</p>
            
            <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 flex flex-col gap-2">
                        <textarea
                            value={optimizationInput}
                            onChange={(e) => {
                                setOptimizationInput(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !isOptimizing && !isLoading) {
                                    e.preventDefault();
                                    handleOptimizePrompt();
                                }
                            }}
                            placeholder={optimizationHistory.length === 0 ? "Zusätzliche Anweisung (z.B. 'Füge Beispiele hinzu', 'Mache es kürzer')…" : "Weitere Anpassungswünsche (z.B. 'Tonalität freundlicher')… ⏎ Senden"}
                            className="input resize-none min-h-[60px]"
                            rows={2}
                        />
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <span className="label-xs">Modell</span>
                                <QuickModelSelector 
                                    currentModel={settings?.optimizerModel || (!settings?.selectedProviderId || settings?.selectedProviderId === 'gemini' ? DEFAULT_GEMINI_MODEL : '')} 
                                    onSelect={(m) => onUpdateSettings?.({ optimizerModel: m })}
                                    providerId={settings?.selectedProviderId || 'gemini'}
                                    customProviders={settings?.customProviders || []}
                                    customGeminiModels={settings?.customGeminiModels || []}
                                />
                            </div>
                            <span className="text-xs text-graphite hidden sm:inline"><kbd className="kbd">⏎</kbd> senden · <kbd className="kbd">⇧</kbd><kbd className="kbd">⏎</kbd> Zeilenumbruch</span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 self-end">
                        <button
                          onClick={handleOptimizePrompt}
                          disabled={isOptimizing || isLoading || (!content.trim() && optimizationHistory.length === 0) || (optimizationHistory.length > 0 && !optimizationInput.trim())}
                          className="btn btn-primary whitespace-nowrap"
                        >
                          {isOptimizing ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <WandIcon className="h-4 w-4" />}
                          <span>{isOptimizing ? 'Wird verbessert …' : (optimizationHistory.length === 0 ? 'Verbessern' : 'Weiter verbessern')}</span>
                        </button>
                        {isOptimizing && (
                          <button
                            onClick={handleAbortOptimize}
                            className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 font-medium hover:bg-red-200 dark:hover:bg-red-500/25 transition-colors text-xs active:scale-95"
                          >
                            <XIcon />
                            <span>Abbrechen</span>
                          </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
      </div>

      {optimizationHistory.length > 0 && (
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <SparklesIcon />
              Optimierungsverlauf
              <span className="text-[10px] font-normal bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                {optimizationHistory.filter(m => m.role === 'assistant').length} {optimizationHistory.filter(m => m.role === 'assistant').length === 1 ? 'Vorschlag' : 'Vorschläge'}
              </span>
            </h3>
            <button
              onClick={() => { setOptimizationHistory([]); setExpandedOptHistory({}); }}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
            >
              <TrashIcon />
              <span>Verlauf löschen</span>
            </button>
          </div>
          <div className="space-y-3">
            {(() => {
              const assistantItems = optimizationHistory.filter(m => m.role === 'assistant');
              return assistantItems.map((msg, index) => {
                const isLatest = index === assistantItems.length - 1;
                // Find the user message that preceded this assistant message
                const assistantAbsIdx = optimizationHistory.lastIndexOf(msg);
                const precedingUser = assistantAbsIdx > 0 ? optimizationHistory[assistantAbsIdx - 1] : null;
                const userInstruction = precedingUser?.role === 'user' && index > 0
                  ? precedingUser.content
                  : null;
                const isExpanded = isLatest || expandedOptHistory[index];

                return (
                  <div key={index} className={`rounded-xl border transition-all ${
                    isLatest
                      ? 'border-green-400 dark:border-green-600 bg-white dark:bg-gray-800 shadow-sm shadow-green-500/10'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30'
                  }`}>
                    {/* Header */}
                    <button
                      onClick={() => !isLatest && setExpandedOptHistory(prev => ({ ...prev, [index]: !prev[index] }))}
                      className={`w-full flex items-center justify-between px-4 py-2.5 ${!isLatest ? 'cursor-pointer hover:bg-gray-100/50 dark:hover:bg-gray-700/30' : 'cursor-default'} rounded-t-xl transition-colors`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 ${isLatest ? 'text-green-500' : 'text-gray-400'}`}><SparklesIcon /></div>
                        <span className={`text-xs font-semibold ${isLatest ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {isLatest ? '✦ Aktueller Vorschlag' : `Vorschlag ${index + 1}`}
                        </span>
                      </div>
                      {!isLatest && (
                        <div className="text-gray-400 dark:text-gray-500">
                          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                      )}
                    </button>

                    {/* User instruction that led to this result */}
                    {isExpanded && userInstruction && (
                      <div className="px-4 py-2 bg-indigo-50/50 dark:bg-indigo-500/5 border-t border-indigo-100 dark:border-indigo-500/10 flex items-start gap-2">
                        <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider shrink-0 mt-0.5">Deine Anweisung:</span>
                        <p className="text-xs text-indigo-700 dark:text-indigo-300 italic">{userInstruction}</p>
                      </div>
                    )}

                    {/* Optimized content */}
                    {isExpanded && (
                      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/50">
                        <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono leading-relaxed">{msg.content}</pre>
                        {isLatest && (
                          <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                            <button
                              onClick={() => handleCopyToClipboard(msg.content, 'Vorschlag kopiert', false)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                              <ClipboardCopyIcon />
                              Kopieren
                            </button>
                            <button
                              onClick={() => {
                                onChangeContent(msg.content);
                                setOptimizationHistory([]);
                                setExpandedOptHistory({});
                                showToast('Prompt aktualisiert!');
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors"
                            >
                              <SparklesIcon />
                              Vorschlag anwenden
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {testChatHistory.length > 0 && (
        <div className="mt-6 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <EyeIcon /> Testergebnis
              <span className="text-[10px] font-normal normal-case bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 rounded-full">
                {settings?.testModel || 'Standard'}
              </span>
            </h3>
            <button
              onClick={() => { setTestChatHistory([]); setTestFollowUp(''); }}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              title="Testergebnis leeren"
            >
              <XIcon />
            </button>
          </div>

          {/* Chat History */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-100 dark:divide-gray-700/50 max-h-[480px] overflow-y-auto scrollbar-thin">
              {testChatHistory.slice(1).map((msg, idx) => (
                <div key={idx} className={`p-4 ${msg.role === 'user' ? 'bg-indigo-50/40 dark:bg-indigo-500/5' : ''}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      msg.role === 'user' ? 'text-indigo-500' : 'text-emerald-500'
                    }`}>
                      {msg.role === 'user' ? '👤 Folgefrage' : '✨ KI-Antwort'}
                    </span>
                  </div>
                  {msg.role === 'assistant' ? (
                    <div className="group relative">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono leading-relaxed">{msg.content}</pre>
                      <button
                        onClick={() => handleCopyToClipboard(msg.content, 'Antwort kopiert', false)}
                        className="mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs text-gray-500 dark:text-gray-400 transition-colors"
                      >
                        <ClipboardCopyIcon />
                        Kopieren
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-indigo-700 dark:text-indigo-300 italic">{msg.content}</p>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="p-4 flex items-center gap-2 text-sm text-gray-400">
                  <span className="animate-spin inline-flex"><Loader2Icon /></span>
                  KI antwortet…
                </div>
              )}
            </div>

            {/* Follow-up input */}
            <div className="border-t border-gray-100 dark:border-gray-700/50 p-3 bg-gray-50/50 dark:bg-gray-800/30">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={testFollowUp}
                  onChange={(e) => setTestFollowUp(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTestFollowUp(); } }}
                  placeholder="Folgefrage stellen… (z.B. 'Erkläre Punkt 2 genauer', 'Erstelle eine Variante')"
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-shadow"
                  disabled={isLoading}
                />
                {isLoading ? (
                  <button
                    onClick={handleAbortTest}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 font-medium hover:bg-red-200 dark:hover:bg-red-500/25 transition-colors text-sm active:scale-95"
                  >
                    <XIcon />
                  </button>
                ) : (
                  <button
                    onClick={handleTestFollowUp}
                    disabled={!testFollowUp.trim()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-500 text-white font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                  >
                    <SendIcon />
                    <span className="hidden sm:inline">Senden</span>
                  </button>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5 px-1">⏎ Senden · Stelle Folgefragen, um das Ergebnis zu verfeinern</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          PLACEHOLDER FILL MODAL — shown before copying
          ═══════════════════════════════════════════════════ */}
      {showPlaceholderModal && (
        <VariableSubstitutionModal
          promptName={label}
          content={content}
          copyMode={copyMode}
          initialValues={placeholderValues}
          onValuesChange={setPlaceholderValues}
          onClose={() => setShowPlaceholderModal(false)}
          onCopy={(substituted) => {
            if (copyMode === 'english') {
              setShowPlaceholderModal(false);
              setIsTranslating(true);
              translateText(substituted, 'de', 'en')
                .then((englishPrompt) => setTranslationForCopy(englishPrompt))
                .catch((error) => {
                  const message = error instanceof Error ? error.message : 'Übersetzung fehlgeschlagen.';
                  showToast(message, 'error');
                })
                .finally(() => setIsTranslating(false));
            } else {
              handleCopyToClipboard(substituted, 'Prompt mit ausgefüllten Platzhaltern kopiert');
              setShowPlaceholderModal(false);
            }
          }}
        />
      )}

      {/* Translation confirmation modal (for prompts without placeholders or after placeholder fill) */}
      {translationForCopy && (
        <Modal title="Englischen Prompt kopieren" onClose={() => setTranslationForCopy(null)}>
          <div>
            <p className="text-sm mb-2 text-gray-600 dark:text-gray-300">Der folgende englische Prompt wird in Ihre Zwischenablage kopiert:</p>
            <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md max-h-60 overflow-y-auto">
              {translationForCopy}
            </pre>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button type="button" onClick={() => setTranslationForCopy(null)} className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Abbrechen</button>
            <button onClick={handleConfirmCopy} className="px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">Kopieren</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PromptActions;
